import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, FileText, Briefcase, Mail, Phone, GraduationCap, ArrowUpRight, X, Stethoscope } from 'lucide-react';
import { useApplications, useOpportunityById } from '../features/opportunities/hooks/use-opportunities';
import { timeAgo } from '../utils/timeAgo';
import AppShell from '../components/layout/AppShell';
import SecureFileLink from '../components/SecureFileLink';

const formatYear = (year) => {
  if (!year && year !== 0) return '';
  const str = String(year).trim();
  if (!str) return '';
  if (/year/i.test(str) || str.toLowerCase() === 'intern') return str;
  return `Year ${str}`;
};

const formatCollege = (college) => {
  if (!college) return '';
  if (typeof college === 'string') return college.trim();
  if (typeof college === 'object' && college.name) return String(college.name).trim();
  return '';
};

const formatExp = (exp) => {
  if (!exp && exp !== 0) return '';
  return `${exp} yrs exp`;
};

const ApplicantsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedResponse, setSelectedResponse] = React.useState(null);
  
  const { data: oppData, isLoading: isLoadingOpp } = useOpportunityById(id);
  const { data: appsData, isLoading: isLoadingApps } = useApplications(id);

  const opportunity = oppData?.data;
  const applications = appsData?.data || [];

  const isLoading = isLoadingOpp || isLoadingApps;

  if (isLoading) {
    return (
      <div className="flex-1 p-8 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-indigo" />
      </div>
    );
  }

  return (
    <AppShell>
      <div className="flex-1 p-8 max-w-7xl mx-auto w-full bg-gray-50 min-h-screen">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-50 rounded-xl flex items-center justify-center">
              <Briefcase size={24} className="text-accent-indigo" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Applicants</h1>
              <p className="text-sm text-gray-600 mt-1">
                {opportunity?.title ? `Reviewing applications for ${opportunity.title}` : 'Reviewing applications'}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-6">
          {applications.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl border border-gray-200">
              <User size={48} className="text-gray-300 mx-auto mb-4" />
              <p className="text-lg font-semibold text-gray-900 mb-2">No applicants yet</p>
              <p className="text-sm text-gray-500">When students apply to this position, their applications will appear here.</p>
            </div>
          ) : (
            /* --- LIST VIEW --- */
            <div className="grid grid-cols-1 gap-6">
              {applications.map((app) => (
                <div key={app.id} className="bg-white rounded-xl border border-gray-100 p-6 shadow-sm relative">
                  
                  {/* Applied Date - Absolute Top Right */}
                  <div className="absolute top-6 right-6 text-sm text-gray-400 font-medium">
                    Applied {timeAgo(app.createdAt)}
                  </div>

                  {/* Header: Avatar and Info */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-semibold text-lg shrink-0">
                      {app.applicantName ? app.applicantName.split(' ').map(n=>n[0]).join('').substring(0,2).toUpperCase() : <User size={24} className="text-gray-400" />}
                    </div>
                    <div className="flex-1 pr-32">
                      <h3 className="text-lg font-semibold text-gray-800">
                        {app.applicantName || 'Unknown Applicant'}
                      </h3>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-1.5 text-sm text-gray-500 font-medium">
                        {(app.email || app.applicantEmail) && (
                          <span className="flex items-center gap-1.5">
                            <Mail size={14} className="text-gray-400" />
                            {app.email || app.applicantEmail}
                          </span>
                        )}
                        {(app.phone || app.applicantPhone) && (
                          <span className="flex items-center gap-1.5">
                            <Phone size={14} className="text-gray-400" />
                            {app.phone || app.applicantPhone}
                          </span>
                        )}
                        {(app.specialty || app.yearsOfExperience) ? (
                          <span className="flex items-center gap-1.5">
                            <Stethoscope size={14} className="text-purple-500" />
                            {[app.specialty, formatExp(app.yearsOfExperience)].filter(Boolean).join(' · ')}
                          </span>
                        ) : (formatCollege(app.college) || app.yearOfStudy) ? (
                          <span className="flex items-center gap-1.5">
                            <GraduationCap size={14} className="text-gray-400" />
                            {[formatCollege(app.college), formatYear(app.yearOfStudy)].filter(Boolean).join(' · ')}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <hr className="my-6 border-gray-50" />

                  {/* Clinical Screening Response (Collapsed) */}
                  <div>
                    <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">
                      Clinical Screening Response
                    </h4>
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                      <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap line-clamp-3">
                        {app.screeningAnswer || 'No response provided.'}
                      </p>
                    </div>
                  </div>

                  {/* Footer Actions */}
                  <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
                    {/* File Chips */}
                    <div className="flex items-center gap-3">
                      {app.resumeUrl && (
                        <SecureFileLink 
                          endpoint={`/opportunities/${id}/applications/${app.id}/resume/download`} 
                          label="Resume" 
                        />
                      )}
                      {app.caseResponseUrl && (
                        <SecureFileLink 
                          endpoint={`/opportunities/${id}/applications/${app.id}/case-response/download`} 
                          label="Case Response" 
                        />
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setSelectedResponse(app)}
                        className="px-4 py-2 text-sm font-semibold text-gray-600 border border-gray-200 rounded-lg hover:text-gray-800 hover:border-gray-300 transition-colors flex items-center gap-1.5"
                      >
                        View response <ArrowUpRight size={14} />
                      </button>
                    </div>
                  </div>

                </div>
              ))}
            </div>
          )}
        </div>

        {/* --- RESPONSE MODAL OVERLAY --- */}
        {selectedResponse && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div 
              className="absolute inset-0 bg-black/40 backdrop-blur-sm" 
              onClick={() => setSelectedResponse(null)} 
            />

            {/* Modal Content Card */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-fadeIn scrollbar-hide">
              
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-100">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 font-semibold text-lg shrink-0">
                    {selectedResponse.applicantName ? selectedResponse.applicantName.split(' ').map(n=>n[0]).join('').substring(0,2).toUpperCase() : <User size={24} className="text-gray-400" />}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      {selectedResponse.applicantName || 'Unknown Applicant'}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs text-gray-500 font-medium">
                      {(selectedResponse.email || selectedResponse.applicantEmail) && (
                        <span className="flex items-center gap-1">
                          <Mail size={12} className="text-gray-400" />
                          {selectedResponse.email || selectedResponse.applicantEmail}
                        </span>
                      )}
                      {(selectedResponse.phone || selectedResponse.applicantPhone) && (
                        <span className="flex items-center gap-1">
                          <Phone size={12} className="text-gray-400" />
                          {selectedResponse.phone || selectedResponse.applicantPhone}
                        </span>
                      )}
                      {(selectedResponse.specialty || selectedResponse.yearsOfExperience) ? (
                        <span className="flex items-center gap-1">
                          <Stethoscope size={12} className="text-purple-500" />
                          {[selectedResponse.specialty, formatExp(selectedResponse.yearsOfExperience)].filter(Boolean).join(' · ')}
                        </span>
                      ) : (formatCollege(selectedResponse.college) || selectedResponse.yearOfStudy) ? (
                        <span className="flex items-center gap-1">
                          <GraduationCap size={12} className="text-gray-400" />
                          {[formatCollege(selectedResponse.college), formatYear(selectedResponse.yearOfStudy)].filter(Boolean).join(' · ')}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedResponse(null)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X size={18} className="text-gray-400" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-6">
                <div>
                  <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">
                    CLINICAL SCREENING QUESTION
                  </h4>
                  <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
                    <p className="text-gray-700 text-[15px] leading-relaxed font-medium whitespace-pre-wrap break-words">
                      {opportunity?.screeningQuestion || 'No question provided for this position.'}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">
                    FULL RESPONSE
                  </h4>
                  <p className="text-gray-700 text-[15px] leading-relaxed whitespace-pre-wrap break-words">
                    {selectedResponse.screeningAnswer || 'No response provided.'}
                  </p>
                </div>

                {/* Files */}
                {(selectedResponse.resumeUrl || selectedResponse.caseResponseUrl) && (
                  <div className="flex items-center gap-3 pt-6 border-t border-gray-100">
                    {selectedResponse.resumeUrl && (
                      <SecureFileLink 
                        endpoint={`/opportunities/${id}/applications/${selectedResponse.id}/resume/download`} 
                        label="Resume" 
                        className="inline-flex items-center gap-2 px-3.5 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 font-medium hover:bg-gray-100 transition-colors"
                      />
                    )}
                    {selectedResponse.caseResponseUrl && (
                      <SecureFileLink 
                        endpoint={`/opportunities/${id}/applications/${selectedResponse.id}/case-response/download`} 
                        label="Case Response" 
                        className="inline-flex items-center gap-2 px-3.5 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600 font-medium hover:bg-gray-100 transition-colors"
                      />
                    )}
                  </div>
                )}
              </div>

            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
};

export default ApplicantsPage;
